/**
 * Settings → Notifications tab content: a master on/off switch plus the 4
 * grouped categories from the owner-lifecycle-mail spec, each with its own
 * toggle. Backed by useOwnerNotificationPreferences
 * (owner_notification_preferences, 20260826090000) — the actual send gate
 * lives server-side in owner-lifecycle-emails.ts's
 * sendAndStampOwnerLifecycleEmail, so a toggle here takes effect on the very
 * next mail of that category, not just this page's own display.
 */
import {
  CalendarCheck,
  ClipboardCheck,
  CreditCard,
  FileCheck,
  Home,
  KeyRound,
  Landmark,
  type LucideIcon,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  useOwnerNotificationPreferences,
  type OwnerNotificationPreferenceKey,
} from "@/hooks/v2/useOwnerNotificationPreferences";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

interface GroupItem {
  key: OwnerNotificationPreferenceKey;
  titleKey: string;
  titleFallback: string;
  subKey: string;
  subFallback: string;
}

interface Group {
  icon: LucideIcon;
  titleKey: string;
  titleFallback: string;
  subKey: string;
  subFallback: string;
  items: GroupItem[];
}

const GROUPS: Group[] = [
  {
    icon: Home,
    titleKey: "owner.notifications.groupOnboarding",
    titleFallback: "Onboarding",
    subKey: "owner.notifications.groupOnboardingSub",
    subFallback: "When your portal is first set up",
    items: [
      {
        key: "portal_ready",
        titleKey: "owner.notifications.itemPortalReady",
        titleFallback: "Owner portal ready",
        subKey: "owner.notifications.itemPortalReadySub",
        subFallback: "Onboarding of a new property completed",
      },
    ],
  },
  {
    icon: KeyRound,
    titleKey: "owner.notifications.groupMoveIn",
    titleFallback: "Tenant move-in",
    subKey: "owner.notifications.groupMoveInSub",
    subFallback: "Lease, deposit, keys and protocol on the way in",
    items: [
      {
        key: "lease_signed",
        titleKey: "owner.notifications.itemLeaseSigned",
        titleFallback: "Lease agreement signed",
        subKey: "owner.notifications.itemLeaseSignedSub",
        subFallback: "A tenant contract for your property has been signed",
      },
      {
        key: "deposit_received",
        titleKey: "owner.notifications.itemDepositReceived",
        titleFallback: "Security deposit received",
        subKey: "owner.notifications.itemDepositReceivedSub",
        subFallback: "Deposit payment received and confirmed",
      },
      {
        key: "keys_handed_over",
        titleKey: "owner.notifications.itemKeysHandedOver",
        titleFallback: "Keys handed over",
        subKey: "owner.notifications.itemKeysHandedOverSub",
        subFallback: "Keys marked as picked up by the tenant",
      },
      {
        key: "move_in_protocol",
        titleKey: "owner.notifications.itemMoveInProtocol",
        titleFallback: "Move-in protocol available",
        subKey: "owner.notifications.itemMoveInProtocolSub",
        subFallback: "A move-in protocol has been submitted",
      },
    ],
  },
  {
    icon: CreditCard,
    titleKey: "owner.notifications.groupRent",
    titleFallback: "Rent payments",
    subKey: "owner.notifications.groupRentSub",
    subFallback: "When a tenant's monthly rent comes in",
    items: [
      {
        key: "rent_received",
        titleKey: "owner.notifications.itemRentReceived",
        titleFallback: "Rent payment received",
        subKey: "owner.notifications.itemRentReceivedSub",
        subFallback: "A tenant's monthly rent payment has been received",
      },
    ],
  },
  {
    icon: CalendarCheck,
    titleKey: "owner.notifications.groupMoveOut",
    titleFallback: "Tenant move-out",
    subKey: "owner.notifications.groupMoveOutSub",
    subFallback: "Confirmation, protocol and keys on the way out",
    items: [
      {
        key: "move_out_date_confirmed",
        titleKey: "owner.notifications.itemMoveOutDate",
        titleFallback: "Move-out date confirmed",
        subKey: "owner.notifications.itemMoveOutDateSub",
        subFallback: "Tenant's move-out date has been confirmed",
      },
      {
        key: "move_out_protocol",
        titleKey: "owner.notifications.itemMoveOutProtocol",
        titleFallback: "Move-out protocol available",
        subKey: "owner.notifications.itemMoveOutProtocolSub",
        subFallback: "A move-out protocol has been submitted",
      },
      {
        key: "keys_returned",
        titleKey: "owner.notifications.itemKeysReturned",
        titleFallback: "Keys returned",
        subKey: "owner.notifications.itemKeysReturnedSub",
        subFallback: "Keys have been returned by the tenant",
      },
    ],
  },
];

export function OwnerNotificationPreferencesCard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { preferences, allOn, isLoading, toggle, toggleAll } =
    useOwnerNotificationPreferences();

  return (
    <div className="space-y-4">
      <Card className="rounded-2xl border-border/60 shadow-sm overflow-hidden">
        <CardHeader className="border-b bg-muted/10 pb-4">
          <CardTitle className="text-lg">
            {t("owner.notifications.emailTitle", "Email notifications")}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {t(
              "owner.notifications.emailDesc",
              "Sent to {email}. Only activity on your own properties."
            ).replace("{email}", user?.email ?? "—")}
          </p>
        </CardHeader>
        <CardContent className="pt-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold">
              {t(
                "owner.notifications.allToggleTitle",
                "All email notifications"
              )}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t(
                "owner.notifications.allToggleSub",
                "Turn every email below on or off at once."
              )}
            </p>
          </div>
          <Switch
            checked={allOn}
            disabled={isLoading}
            onCheckedChange={(checked) => toggleAll(checked)}
            aria-label={t(
              "owner.notifications.allToggleTitle",
              "All email notifications"
            )}
          />
        </CardContent>
      </Card>

      {GROUPS.map((group) => {
        const Icon = group.icon;
        return (
          <Card
            key={group.titleFallback}
            className="rounded-2xl border-border/60 shadow-sm overflow-hidden"
          >
            <CardHeader className="flex-row items-center gap-3 border-b bg-muted/10 py-3.5 space-y-0">
              <div className="h-8 w-8 rounded-lg border border-border/60 bg-background grid place-items-center text-primary shrink-0">
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-sm">
                  {t(group.titleKey, group.titleFallback)}
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t(group.subKey, group.subFallback)}
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-0 divide-y divide-border/60">
              {group.items.map((item) => (
                <div
                  key={item.key}
                  className="flex items-center justify-between gap-4 px-5 py-3.5"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {t(item.titleKey, item.titleFallback)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t(item.subKey, item.subFallback)}
                    </p>
                  </div>
                  <Switch
                    checked={preferences[item.key]}
                    disabled={isLoading}
                    onCheckedChange={(checked) => toggle(item.key, checked)}
                    aria-label={t(item.titleKey, item.titleFallback)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
